import { shortDay, dayLabel, dayTime } from "./initialAssessment";
import { isFped } from "./regime";

/**
 * The initial assessment, as the team sees it in the admin (15.10.2026): the
 * switch, the choice per group, the states and the questions before sending.
 * The words were validated in docs/texte-evaluare-initiala-2026-10-15.md
 * (parts 8 and 10). The server decides and enforces; this only words it.
 */

export const MODES = [
  { key: "automat", label: "Automat", hint: "fiecare cursant o primește pe email după contractul semnat." },
  { key: "manual", label: "Manual", hint: "o trimiți tu din fișa grupei, când vrei." },
  { key: "external", label: "Nu prin platformă",
    hint: "grupa a completat-o deja în altă parte, de exemplu în Google Forms." },
];

export const SWITCH_TITLE = "Trimite automat evaluarea inițială.";
export const SWITCH_HINT =
  "Cursanții cursului acreditat cu loc în grupă și contractul semnat primesc chestionarul pe email, între orele 08 și 21, "
  + "la grupele cu varianta „Automat”. Cine nu răspunde primește o reamintire în cele 48 de ore dinaintea primei sesiuni "
  + "și una a doua zi după ea. Grupele Curs FPED nu îl primesc.";

export const LEVEL_WORDS = { beginner: "Începător", intermediate: "Intermediar", advanced: "Avansat" };

export const ORIENTATIVE =
  "Nivelul e orientativ: e calculat din 6 întrebări de autoevaluare. E un punct de pornire, nu o măsurătoare; "
  + "discuția din prima sesiune spune mai mult.";

const people = (n) => (n === 1 ? "un cursant" : `${n} cursanți`);

/** "Automat", "Nu prin platformă (Google Forms, 3 nov.)", or "Nealeasă". */
export function modeLine(mode) {
  const found = MODES.find((m) => m.key === mode?.mode);
  if (!found) return "Nealeasă";
  return found.key === "external" && mode?.note ? `${found.label} (${mode.note})` : found.label;
}

/**
 * One questionnaire's state, in a few words, and whether it is the red one.
 * `state` is the server's: not_sent, open, completed or closed.
 */
export function stateLine(state, record) {
  const r = record || {};
  if (state === "completed") {
    if (r.after_reopen) return { text: "Completat după redeschidere", tone: "ok" };
    if (r.after_start) return { text: "Completat după începerea cursului", tone: "ok" };
    return { text: `Completat pe ${shortDay(r.completed_at)}`, tone: "ok" };
  }
  if (state === "closed") return { text: "Închis, necompletat", tone: "bad" };
  if (state === "open") {
    if ((r.reopens || []).length > 0) return { text: `Redeschis până pe ${shortDay(r.deadline)}`, tone: "wait" };
    return { text: `Trimis pe ${shortDay(r.sent_at)}`, tone: "wait" };
  }
  return { text: "Netrimis", tone: "none" };
}

/** Everybody who has not answered yet — not sent, open or closed. */
export function missingLine(counts) {
  const missing = Math.max(0, (counts?.people || 0) - (counts?.completed || 0));
  return `${missing} din ${counts?.people || 0} cursanți nu au completat evaluarea inițială.`;
}

export const reopenAllLabel = (n) => `Redeschide pentru toți cei care lipsesc (${n})`;

export const reopenAllQuestion = (n, deadline) =>
  `Redeschizi chestionarul pentru ${people(n)} și le trimiți câte un link nou, cu termen până pe ${shortDay(deadline)}.`;

export const sendGroupLabel = (n) =>
  `Trimite chestionarul (${people(n)} cu contract semnat care nu l-au primit)`;

export const sendGroupQuestion = (n, deadline) =>
  `Trimiți chestionarul către ${people(n)}? Îl pot completa până pe ${dayLabel(deadline)}.`;

export const lateStartLine = (deadline) =>
  `Grupa a trecut de a doua sesiune, așa că primesc 7 zile, până pe ${shortDay(deadline)}. Poți schimba termenul.`;

export const unsetWarning = (firstSession) =>
  `⚠ Alege dacă se trimite evaluarea inițială: grupa începe pe ${shortDay(firstSession)}. Până alegi, nu pleacă nimic.`;

export const resendQuestion = (email) => `Trimiți din nou linkul către ${email}? Linkul vechi nu mai merge.`;

/** The reopening dialog: `days` is null when the team picked a date of its own. */
export function reopenQuestion(name, deadline, days) {
  const term = days ? `${days} zile, până pe ${shortDay(deadline)}` : `până pe ${shortDay(deadline)}`;
  return `Redeschizi chestionarul pentru ${name} și îi trimiți un link nou. Termenul: ${term} (îl poți schimba). `
    + "Linkul vechi nu mai merge.";
}

export const requestLine = (name, at) => `${name} a cerut redeschiderea pe ${dayTime(at)}.`;

export const startedNote = (firstSession) =>
  `Grupa a început pe ${shortDay(firstSession)}. Dacă a completat deja formularul, alege „Nu prin platformă”, `
  + "ca să nu-l primească a doua oară.";

/** The first session's day, "2026-11-03": the earliest dated session, else the start date. */
export function firstDay(group) {
  const days = (group?.sessions || []).map((s) => s?.date).filter(Boolean).sort();
  return days[0] || group?.start_date || null;
}

const ymd = (date) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(date);

/** Today in Romania, "2026-10-15". */
export const today = (now = new Date()) => ymd(now);

/** `days` from now, as a date input wants it. */
export function plusDays(days, now = new Date()) {
  return ymd(new Date(now.getTime() + days * 86400000));
}

/** "2026-11-05" of an ISO moment, by Romania's clock: a date input's value. */
export const dayOf = (iso) => (iso ? ymd(new Date(iso)) : "");

/** A day as an ISO moment the helpers above can word: noon, so no clock moves it a day. */
export const dayAsIso = (day) => (day ? `${day}T12:00:00+03:00` : null);

export function started(group, now = new Date()) {
  const first = firstDay(group);
  return Boolean(first) && first <= today(now);
}

/** What comes ticked when the portal is opened (decision F): Automat, or "Nu prin platformă" once begun. */
export function defaultMode(group, now = new Date()) {
  return started(group, now) ? "external" : "automat";
}

/**
 * The ⚠ in the list of groups: an accredited group with no choice made that
 * starts within 14 days. Past its start, the dossier is the team's call.
 */
export function needsChoice(group, now = new Date()) {
  if (!group || isFped(group) || group?.initial_assessment?.mode) return false;
  const first = firstDay(group);
  return Boolean(first) && first >= today(now) && first <= plusDays(14, now);
}

/** One answer as the team reads it, from the questionnaire the server sent. */
export function answerText(question, answers) {
  const a = answers || {};
  const value = a[question.id];
  const label = (key) => (question.options || []).find((o) => o.key === key)?.label || key;
  if (question.type === "multi") {
    const other = a[`${question.id}_other`];
    return (value || [])
      .map((key) => (key === question.other && other ? `${label(key)}: ${other}` : label(key)))
      .join("; ");
  }
  if (question.type === "single") return value ? label(value) : "";
  if (question.type === "scale") return value ? `${value} din ${question.max}` : "";
  return value == null ? "" : String(value);
}

const EVENT_WORDS = {
  sent: "Trimis",
  resent: "Retrimis, cu un link nou",
  reopened: "Redeschis",
  completed: "Completat",
  reopen_requested: "Cursantul a cerut redeschiderea",
};

/** A line of the questionnaire's history: what, when, and by whom when it was the team. */
export function eventLine(event) {
  const what = EVENT_WORDS[event?.type] || event?.type || "";
  const until = event?.type === "reopened" && event?.details?.deadline ? ` până pe ${shortDay(event.details.deadline)}` : "";
  const who = event?.actor && !["completed", "reopen_requested"].includes(event?.type) ? ` · ${event.actor}` : "";
  return `${dayTime(event?.at)} · ${what}${until}${who}`;
}

/** The trainer's "Nivel inițial (orientativ)" cell; the marker is for an answer given once the course began. */
export function levelCell(row) {
  if (!row?.completed) return { text: "Necompletat", missing: true, afterStart: false };
  return { text: LEVEL_WORDS[row.level] || "—", missing: false, afterStart: Boolean(row.after_start) };
}
